import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../lib/auth.tsx";
import { AuthShell, Field } from "./Login.tsx";

const FormSchema = z.object({
  email: z.string().email(),
  code: z
    .string()
    .regex(/^\d{6}$/, "Six digits"),
});
type FormValues = z.infer<typeof FormSchema>;

export function ConfirmSignUpPage() {
  const { confirmSignUp, resendCode } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetEmail = searchParams.get("email") ?? "";
  const presetCode = searchParams.get("code") ?? "";
  const [serverError, setServerError] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<string | null>(null);
  // True when we arrived via a verification link AND we're still working
  // through the auto-submit; lets us show a friendly loading state instead
  // of the form for a beat.
  const [autoVerifying, setAutoVerifying] = useState(
    !!presetEmail && /^\d{6}$/.test(presetCode),
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { email: presetEmail, code: presetCode },
  });

  const email = watch("email");

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await confirmSignUp({ email: values.email, code: values.code });
      navigate(`/login`, { replace: true });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Confirmation failed");
      setAutoVerifying(false);
    }
  });

  // Auto-submit when the page is opened via the email link (both email +
  // 6-digit code in the URL). React StrictMode mounts twice in dev — the
  // ref guard ensures we don't fire confirmSignUp twice in that case.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (!autoVerifying || autoFiredRef.current) return;
    autoFiredRef.current = true;
    void onSubmit();
  }, [autoVerifying, onSubmit]);

  const onResend = async () => {
    setResendStatus(null);
    setServerError(null);
    try {
      await resendCode(email);
      setResendStatus("A new code is on its way.");
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : "Couldn't resend the code",
      );
    }
  };

  if (autoVerifying && !serverError) {
    return (
      <AuthShell
        title="Verifying your email…"
        subtitle="One moment, finishing up."
      >
        <p className="text-center text-sm text-slate-500">
          You'll be redirected to sign in shortly.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Confirm your email"
      subtitle="Click the link in the email, or enter the 6-digit code below."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Email" error={errors.email?.message}>
          <input
            type="email"
            autoComplete="email"
            {...register("email")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <Field label="Verification code" error={errors.code?.message}>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            {...register("code")}
            placeholder="123456"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-center font-mono text-lg tracking-[0.5em] focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>

        {serverError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
            {serverError}
          </p>
        )}
        {resendStatus && (
          <p className="rounded-md bg-brand-50 px-3 py-2 text-xs text-brand-700">
            {resendStatus}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {isSubmitting ? "Verifying…" : "Verify email"}
        </button>
      </form>

      <div className="mt-4 flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={onResend}
          className="text-brand-700 hover:underline"
        >
          Resend code
        </button>
        <Link to="/login" className="text-slate-500 hover:underline">
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
